import React, { useState } from 'react';
import styled from 'styled-components';
import DragIndicatorIcon from '@material-ui/icons/DragIndicator';

import {
  Button,
  List,
  ListItem,
  ListItemIcon,
  ListItemText
} from '@material-ui/core';
import AddIcon from '@material-ui/icons/Add';
import {
  StateTransition,
  StateTransitionCondition,
  StateEvent
} from '@sierralabs/workflow';

import StateEventConditionDialog from '../dialogs/StateEventConditionDialog';
import { WorkflowTaskTypeEntity } from '../Workflow.interface';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import transitionConditionPlugins from '../plugins/transition-conditions';

export const Styled = {
  StateEventConditions: styled.div`
    margin-top: 10px;
    .add-condition {
      width: 100%;
    }
    .drag-icon {
      min-width: auto;
      padding-right: 10px;
    }
  `
};

const StateEventConditions: React.FC<{
  event: StateEvent;
  type?: WorkflowTaskTypeEntity;
  transition: StateTransition;
  onChange?: (transition: StateTransition) => void;
}> = ({ event, type, transition: initialTransition, onChange }) => {
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedConditionIndex, setSelectedConditionIndex] = useState(-1);
  const [transition, setTransition] = useState(initialTransition);
  return (
    <Styled.StateEventConditions>
      <DragDropContext
        onDragEnd={result => {
          const { source, destination } = result;
          // dropped outside the list
          if (!destination || source.droppableId !== destination.droppableId) {
            return;
          }
          if (transition.conditions) {
            const deleted = transition.conditions.splice(source.index, 1);
            transition.conditions.splice(destination.index, 0, ...deleted);
            setTransition({ ...transition });
            onChange?.(transition);
          }
        }}
      >
        <Droppable droppableId='conditions'>
          {provided => (
            <List dense ref={provided.innerRef}>
              {transition.conditions &&
                transition.conditions.map((condition, index) => {
                  const plugin = transitionConditionPlugins.find(
                    p => p.type === condition.type
                  );
                  return (
                    <Draggable
                      key={index}
                      index={index}
                      draggableId={`condition-${index}`}
                    >
                      {providedInner => (
                        <ListItem
                          ref={providedInner.innerRef}
                          button
                          onClick={() => {
                            setSelectedConditionIndex(index);
                            setOpenDialog(true);
                          }}
                          {...providedInner.draggableProps}
                          {...providedInner.dragHandleProps}
                        >
                          <ListItemIcon className='drag-icon'>
                            <DragIndicatorIcon />
                          </ListItemIcon>
                          <ListItemText
                            primary={plugin?.label || condition.type}
                          />
                        </ListItem>
                      )}
                    </Draggable>
                  );
                })}
              {provided.placeholder}
            </List>
          )}
        </Droppable>
      </DragDropContext>
      <Button
        className='add-condition'
        variant='outlined'
        startIcon={<AddIcon />}
        onClick={() => setOpenDialog(true)}
      >
        Add Condition
      </Button>
      {openDialog && (
        <StateEventConditionDialog
          event={event}
          type={type}
          condition={
            transition.conditions && selectedConditionIndex >= 0
              ? transition.conditions[selectedConditionIndex]
              : undefined
          }
          open={openDialog}
          onCancel={() => {
            setOpenDialog(false);
            setSelectedConditionIndex(-1);
          }}
          onChange={condition => {
            if (!transition.conditions) {
              transition.conditions = [];
            }
            if (selectedConditionIndex >= 0) {
              transition.conditions[selectedConditionIndex] = condition;
              setSelectedConditionIndex(-1);
              setTransition({ ...transition });
            } else {
              transition.conditions.push(condition);
              setTransition({ ...transition });
            }
            onChange?.(transition);
            setOpenDialog(false);
          }}
          onDelete={() => {
            setOpenDialog(false);
            if (transition.conditions && selectedConditionIndex >= 0) {
              transition.conditions.splice(selectedConditionIndex, 1);
              setSelectedConditionIndex(-1);
              setTransition({ ...transition });
              onChange?.(transition);
            }
          }}
        />
      )}
    </Styled.StateEventConditions>
  );
};

export default StateEventConditions;
